import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiMapPin, FiCalendar, FiDollarSign, FiUsers, FiArrowLeft, FiClock, FiNavigation, FiX } from 'react-icons/fi';
import { formatarData } from '../../utils/dateUtils';
import { getImageUrl, getYouTubeEmbedUrl } from '../../utils/imageUtils';
import toast from 'react-hot-toast';

const ViagemDetalhes = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [viagem, setViagem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imagemAtual, setImagemAtual] = useState(0);

  const handleReservar = () => {
    // Sempre redirecionar para pré-reserva, sem necessidade de cadastro/login
    navigate(`/reservar/${id}`);
  };

  useEffect(() => {
    fetchViagem();
  }, [id]);

  const fetchViagem = async () => {
    try {
      const response = await axios.get(`/api/viagens/public/${id}`);
      setViagem(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes da viagem');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600"></div>
      </div>
    );
  }

  if (!viagem) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">Viagem não encontrada</h2>
        <Link to="/" className="text-sky-600 hover:text-sky-700">
          Voltar para a página inicial
        </Link>
      </div>
    );
  }

  const imagens = viagem.imagens && Array.isArray(viagem.imagens) && viagem.imagens.length > 0
    ? viagem.imagens.map(img => getImageUrl(img))
    : [];
  const videoEmbedUrl = getYouTubeEmbedUrl(viagem.video_url);

  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center text-sky-600 hover:text-sky-700">
            <FiArrowLeft className="mr-2" size={18} />
            Voltar para viagens
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Imagens e Vídeo */}
        <div className="mb-8">
          {imagens.length > 0 || videoEmbedUrl ? (
            <div className="relative h-96 rounded-xl overflow-hidden bg-gray-200">
              {videoEmbedUrl && imagemAtual === 0 ? (
                <iframe
                  src={videoEmbedUrl}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={viagem.titulo}
                />
              ) : imagens.length > 0 ? (
                <>
                  <img
                    src={imagens[imagemAtual] || imagens[0]}
                    alt={viagem.titulo}
                    className="w-full h-full object-cover"
                  />
                  {imagens.length > 1 && (
                    <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex gap-2">
                      {imagens.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setImagemAtual(index)}
                          className={`w-3 h-3 rounded-full ${
                            imagemAtual === index ? 'bg-white' : 'bg-white/50'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          ) : (
            <div className="h-96 bg-gradient-to-br from-sky-100 to-sky-200 rounded-xl flex items-center justify-center">
              <FiMapPin className="text-sky-400" size={64} />
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Conteúdo Principal */}
          <div className="lg:col-span-2">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">{viagem.titulo}</h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              <div className="flex items-center text-gray-700">
                <FiMapPin className="mr-3 text-sky-600" size={20} />
                <span className="font-medium">Destino:</span>
                <span className="ml-2">{viagem.destino}</span>
              </div>
              <div className="flex items-center text-gray-700">
                <FiCalendar className="mr-3 text-sky-600" size={20} />
                <span className="font-medium">Data de Ida:</span>
                <span className="ml-2">{formatarData(viagem.data_ida)}</span>
              </div>
              {viagem.data_volta && (
                <div className="flex items-center text-gray-700">
                  <FiCalendar className="mr-3 text-sky-600" size={20} />
                  <span className="font-medium">Data de Volta:</span>
                  <span className="ml-2">{formatarData(viagem.data_volta)}</span>
                </div>
              )}
              <div className="flex items-center text-gray-700">
                <FiUsers className="mr-3 text-sky-600" size={20} />
                <span className="font-medium">Poltronas:</span>
                <span className={`ml-2 ${viagem.lotado ? 'text-red-600 font-semibold' : ''}`}>
                  {viagem.lotado ? 'Lotado - Não há mais poltronas' : (viagem.vagas_disponiveis || viagem.vagas || 0) + ' disponíveis'}
                </span>
              </div>
              {viagem.local_embarque && (
                <div className="flex items-center text-gray-700">
                  <FiNavigation className="mr-3 text-sky-600" size={20} />
                  <span className="font-medium">Embarque:</span>
                  <span className="ml-2">{viagem.local_embarque}</span>
                </div>
              )}
              {viagem.horario_embarque && (
                <div className="flex items-center text-gray-700">
                  <FiClock className="mr-3 text-sky-600" size={20} />
                  <span className="font-medium">Horário:</span>
                  <span className="ml-2">{viagem.horario_embarque}</span>
                </div>
              )}
            </div>

            {viagem.roteiro && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Roteiro</h2>
                <div className="bg-sky-50 p-6 rounded-lg border border-sky-200">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {viagem.roteiro}
                  </p>
                </div>
              </div>
            )}

            {viagem.inclusos && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">O que está incluso no pacote</h2>
                <div className="bg-green-50 p-6 rounded-lg border border-green-200">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {viagem.inclusos}
                  </p>
                </div>
              </div>
            )}

            {viagem.info_embarque && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Informações Adicionais de Embarque</h2>
                <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {viagem.info_embarque}
                  </p>
                </div>
              </div>
            )}

            {viagem.descricao && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Forma de Pagamento</h2>
                <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {viagem.descricao}
                  </p>
                </div>
              </div>
            )}

            {viagem.direitos_deveres && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Direitos e Deveres dos Passageiros</h2>
                <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {viagem.direitos_deveres}
                  </p>
                </div>
              </div>
            )}

            {viagem.aviso_importante && (
              <div className="mb-8">
                <h2 className="text-2xl font-bold text-red-600 mb-4">⚠️ Aviso Importante</h2>
                <div className="bg-red-50 p-6 rounded-lg border-2 border-red-200">
                  <p className="text-gray-800 leading-relaxed whitespace-pre-line font-medium">
                    {viagem.aviso_importante}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Card de Reserva */}
          <div className="lg:col-span-1">
            <div className="bg-gradient-to-br from-sky-50 to-sky-100 rounded-xl p-6 border-2 border-sky-200 sticky top-24">
              <div className="text-center mb-6">
                <p className="text-sm text-gray-600 mb-2">A partir de</p>
                <p className="text-5xl font-bold text-sky-700">
                  R$ {parseFloat(viagem.valor || 0).toFixed(2)}
                </p>
                <p className="text-sm text-gray-600 mt-2">por pessoa</p>
              </div>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-700">
                  <span>Poltronas Disponíveis:</span>
                  <span className={`font-semibold ${viagem.lotado ? 'text-red-600' : ''}`}>
                    {viagem.lotado ? 'Lotado' : (viagem.vagas_disponiveis || viagem.vagas || 0)}
                  </span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Tipo de ônibus:</span>
                  <span className="font-semibold">
                    {viagem.tipo_onibus === 'convencional' ? 'Convencional (50 lugares)' : 'Double Decker Semi-Leito (64 lugares)'}
                  </span>
                </div>
              </div>

              {viagem.lotado ? (
                <div className="block w-full text-center px-6 py-4 bg-gray-400 text-white rounded-lg font-bold text-lg cursor-not-allowed mb-4">
                  Lotado - Não há mais poltronas
                </div>
              ) : (
                <button
                  onClick={handleReservar}
                  className="w-full text-center px-6 py-4 bg-yellow-400 text-black rounded-lg font-bold text-lg hover:bg-yellow-500 transition-colors mb-4"
                >
                  Solicitar Pré-Reserva
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViagemDetalhes;
